import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useOutlet } from '../../context/OutletContext';
import notificationSound from '../utils/notificationSound';
import orderNotificationService from '../../services/orderNotificationService';
import logger from '../../utils/logger';

const OrderContext = createContext();

export const useOrders = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrders must be used within OrderProvider');
  }
  return context;
};

export const OrderProvider = ({ children }) => {
  const { selectedOutlet, viewMode } = useOutlet(); // Get selected outlet and view mode
  const [orders, setOrders] = useState([]); // Orders for selected outlet or all outlets
  const [allOutletOrders, setAllOutletOrders] = useState([]); // Orders from all outlets (for stats)
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dateFilterEnabled, setDateFilterEnabled] = useState(true); // Toggle for date filter
  const prevOrderIdsRef = useRef(new Set());
  const isInitialLoadRef = useRef(true);

  // Subscribe to orders in real-time (filtered by outlet or all outlets)
  useEffect(() => {
    // Wait for outlet to be selected (unless viewing all outlets)
    if (!selectedOutlet && viewMode !== 'all') {
      setLoading(false);
      return;
    }

    let q;

    if (viewMode === 'all') {
      // Get orders from all outlets
      if (dateFilterEnabled) {
        const startOfDay = new Date(selectedDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(selectedDate);
        endOfDay.setHours(23, 59, 59, 999);

        q = query(
          collection(db, 'orders'),
          where('timestamp', '>=', Timestamp.fromDate(startOfDay)),
          where('timestamp', '<=', Timestamp.fromDate(endOfDay)),
          orderBy('timestamp', 'desc')
        );
      } else {
        q = query(
          collection(db, 'orders'),
          orderBy('timestamp', 'desc')
        );
      }
    } else {
      // Get orders for selected outlet only
      if (dateFilterEnabled) {
        const startOfDay = new Date(selectedDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(selectedDate);
        endOfDay.setHours(23, 59, 59, 999);

        q = query(
          collection(db, 'orders'),
          where('outletId', '==', selectedOutlet.id),
          where('timestamp', '>=', Timestamp.fromDate(startOfDay)),
          where('timestamp', '<=', Timestamp.fromDate(endOfDay)),
          orderBy('timestamp', 'desc')
        );
      } else {
        q = query(
          collection(db, 'orders'),
          where('outletId', '==', selectedOutlet.id),
          orderBy('timestamp', 'desc')
        );
      }
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Detect new orders and play notification sound
      if (!isInitialLoadRef.current) {
        const currentOrderIds = new Set(ordersData.map(order => order.id));
        const newOrders = ordersData.filter(order => !prevOrderIdsRef.current.has(order.id));

        // Play sound only for new orders (not on date change or initial load)
        if (newOrders.length > 0 && prevOrderIdsRef.current.size > 0) {
          logger.info(`🔔 ${newOrders.length} new order(s) detected!`);
          notificationSound.playAlarmSound();

          // Show browser notification if permitted
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('New Order Received! 🍛', {
              body: `${newOrders.length} new order(s) arrived`,
              icon: '/favicon.ico',
              tag: 'new-order',
              requireInteraction: true
            });
          }
        }

        prevOrderIdsRef.current = currentOrderIds;
      } else {
        // First load - just store the order IDs
        prevOrderIdsRef.current = new Set(ordersData.map(order => order.id));
        isInitialLoadRef.current = false;
      }

      // Check for pending acceptance orders and manage continuous alarm
      const pendingOrders = ordersData.filter(order =>
        order.acceptanceStatus === 'pending' ||
        (!order.acceptanceStatus && order.status === 'pending')
      );

      if (pendingOrders.length > 0) {
        // Start continuous alarm if there are pending orders
        if (!notificationSound.isPlayingContinuously()) {
          logger.info(`🔔 ${pendingOrders.length} pending order(s) - starting continuous alarm`);
          notificationSound.startContinuousAlarm();
        }
      } else {
        // Stop continuous alarm if no pending orders
        if (notificationSound.isPlayingContinuously()) {
          logger.info(`🔕 No pending orders - stopping continuous alarm`);
          notificationSound.stopContinuousAlarm();
        }
      }

      if (viewMode === 'all') {
        setAllOutletOrders(ordersData);
        setOrders(ordersData); // Show all orders when viewing all outlets
      } else {
        setOrders(ordersData);
      }
      setLoading(false);
    }, (error) => {
      logger.error('Error fetching orders:', error);
      setLoading(false);
    });

    return () => {
      unsubscribe();
      // Stop continuous alarm when component unmounts
      notificationSound.stopContinuousAlarm();
    };
  }, [selectedDate, dateFilterEnabled, selectedOutlet, viewMode]);

  // Add new order (when order comes from WhatsApp or phone)
  const addOrder = async (orderData) => {
    try {
      // Ensure outlet information is included
      if (!orderData.outletId && selectedOutlet) {
        orderData.outletId = selectedOutlet.id;
        orderData.outletName = selectedOutlet.name;
      }

      const newOrder = {
        ...orderData,
        timestamp: Timestamp.now(),
        status: 'in_progress',
        timeline: {
          orderPlaced: Timestamp.now()
        }
      };

      const docRef = await addDoc(collection(db, 'orders'), newOrder);
      logger.info(`✅ Order created for outlet: ${orderData.outletName || selectedOutlet?.name}`);
      return { success: true, orderId: docRef.id };
    } catch (error) {
      logger.error('Error adding order:', error);
      return { success: false, error };
    }
  };

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      const updateData = {
        status: newStatus,
        [`timeline.${newStatus}`]: Timestamp.now()
      };

      await updateDoc(orderRef, updateData);

      // Send notification to customer
      try {
        // Get the full order data for notification
        const orderDoc = await getDoc(orderRef);
        if (orderDoc.exists()) {
          const orderData = { id: orderDoc.id, ...orderDoc.data() };
          // Send notification asynchronously (don't await to avoid blocking)
          orderNotificationService.notifyStatusChange(orderData, newStatus)
            .then(result => {
              if (result.success) {
                logger.info(`📱 Customer notified of status change: ${newStatus}`);
              }
            })
            .catch(err => logger.error('Notification error:', err));
        }
      } catch (notifyError) {
        // Don't fail the status update if notification fails
        logger.error('Error sending notification:', notifyError);
      }

      return { success: true };
    } catch (error) {
      logger.error('Error updating order status:', error);
      return { success: false, error };
    }
  };

  // Assign delivery boy and update status to out_for_delivery
  const assignDeliveryBoy = async (orderId, deliveryBoyData) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      const updateData = {
        status: 'out_for_delivery',
        deliveryBoy: deliveryBoyData,
        [`timeline.out_for_delivery`]: Timestamp.now()
      };

      await updateDoc(orderRef, updateData);

      // Send notification to customer about delivery assignment
      try {
        const orderDoc = await getDoc(orderRef);
        if (orderDoc.exists()) {
          const orderData = { id: orderDoc.id, ...orderDoc.data() };
          // Send both status update and delivery assignment notifications
          orderNotificationService.notifyDeliveryAssigned(orderData, deliveryBoyData)
            .then(result => {
              if (result.success) {
                logger.info(`📱 Customer notified of delivery partner assignment`);
              }
            })
            .catch(err => logger.error('Notification error:', err));
        }
      } catch (notifyError) {
        logger.error('Error sending notification:', notifyError);
      }

      return { success: true };
    } catch (error) {
      logger.error('Error updating order:', error);
      return { success: false, error };
    }
  };

  // Cancel order
  const cancelOrder = async (orderId, reason) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        status: 'cancelled',
        cancelReason: reason,
        'timeline.cancelled': Timestamp.now()
      });
      return { success: true };
    } catch (error) {
      logger.error('Error cancelling order:', error);
      return { success: false, error };
    }
  };

  // Get orders by status
  const getOrdersByStatus = (status) => {
    return orders.filter(order => order.status === status);
  };

  // Get pending acceptance orders
  const getPendingAcceptanceOrders = () => {
    return orders.filter(order =>
      order.acceptanceStatus === 'pending' ||
      (!order.acceptanceStatus && order.status === 'pending')
    );
  };

  // Accept order
  const acceptOrder = async (orderId) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      const updateData = {
        acceptanceStatus: 'accepted',
        acceptedAt: Timestamp.now(),
        status: 'in_progress', // Move to in_progress after acceptance
        'timeline.accepted': Timestamp.now(),
        'timeline.in_progress': Timestamp.now()
      };

      await updateDoc(orderRef, updateData);
      logger.info(`✅ Order ${orderId} accepted`);

      // Stop notification sound
      notificationSound.stopSound();

      // Send notification to customer about order acceptance
      try {
        const orderDoc = await getDoc(orderRef);
        if (orderDoc.exists()) {
          const orderData = { id: orderDoc.id, ...orderDoc.data() };
          // Send "confirmed" status notification (order accepted)
          orderNotificationService.notifyStatusChange(orderData, 'confirmed')
            .then(result => {
              if (result.success) {
                logger.info(`📱 Customer notified: Order accepted and being prepared`);
              }
            })
            .catch(err => logger.error('Notification error:', err));
        }
      } catch (notifyError) {
        logger.error('Error sending acceptance notification:', notifyError);
      }

      return { success: true };
    } catch (error) {
      logger.error('Error accepting order:', error);
      return { success: false, error };
    }
  };

  // Reject order
  const rejectOrder = async (orderId, reason = 'Order rejected by restaurant') => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      const updateData = {
        acceptanceStatus: 'rejected',
        status: 'cancelled',
        cancelReason: reason,
        rejectedAt: Timestamp.now(),
        'timeline.rejected': Timestamp.now(),
        'timeline.cancelled': Timestamp.now()
      };

      await updateDoc(orderRef, updateData);
      logger.info(`❌ Order ${orderId} rejected: ${reason}`);

      // Send notification to customer about rejection
      try {
        const orderDoc = await getDoc(orderRef);
        if (orderDoc.exists()) {
          const orderData = { id: orderDoc.id, ...orderDoc.data() };
          orderNotificationService.notifyStatusChange(orderData, 'cancelled')
            .then(result => {
              if (result.success) {
                logger.info(`📱 Customer notified of order rejection`);
              }
            })
            .catch(err => logger.error('Notification error:', err));
        }
      } catch (notifyError) {
        logger.error('Error sending notification:', notifyError);
      }

      return { success: true };
    } catch (error) {
      logger.error('Error rejecting order:', error);
      return { success: false, error };
    }
  };

  // Get order by ID (useful for detail pages)
  const getOrderById = (orderId) => {
    return orders.find(order => order.id === orderId);
  };

  // Get orders for specific date (useful for calendar/analytics)
  const getOrdersByDate = (date) => {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    return orders.filter(order => {
      const orderDate = order.timestamp?.toDate();
      return orderDate >= startOfDay && orderDate <= endOfDay;
    });
  };

  // Calculate stats (can be for selected outlet or all outlets)
  const getStats = (outletId = null) => {
    const ordersToAnalyze = outletId
      ? orders.filter(o => o.outletId === outletId)
      : orders;

    const totalOrders = ordersToAnalyze.length;
    const totalRevenue = ordersToAnalyze
      .filter(o => o.status !== 'cancelled')
      .reduce((sum, order) => sum + (order.pricing?.total || 0), 0);

    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    const getStatusCount = (status) =>
      ordersToAnalyze.filter(o => o.status === status).length;

    return {
      totalOrders,
      totalRevenue,
      avgOrderValue,
      inProgress: getStatusCount('in_progress'),
      prepared: getStatusCount('prepared'),
      outForDelivery: getStatusCount('out_for_delivery'),
      delivered: getStatusCount('delivered'),
      cancelled: getStatusCount('cancelled')
    };
  };

  // Get stats for all outlets (grouped by outlet)
  const getAllOutletsStats = () => {
    const outletStats = {};

    allOutletOrders.forEach(order => {
      const outletId = order.outletId;
      if (!outletStats[outletId]) {
        outletStats[outletId] = {
          outletId,
          outletName: order.outletName || 'Unknown Outlet',
          totalOrders: 0,
          totalRevenue: 0,
          cancelled: 0
        };
      }

      outletStats[outletId].totalOrders++;
      if (order.status !== 'cancelled') {
        outletStats[outletId].totalRevenue += order.pricing?.total || 0;
      } else {
        outletStats[outletId].cancelled++;
      }
    });

    return Object.values(outletStats);
  };

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  };

  const value = {
    orders, // Orders (filtered by outlet/date or all outlets based on viewMode)
    allOutletOrders, // All orders from all outlets (for comparison stats)
    loading,
    selectedDate,
    setSelectedDate,
    dateFilterEnabled,
    setDateFilterEnabled, // Toggle date filter on/off
    addOrder,
    updateOrderStatus,
    assignDeliveryBoy, // Assign delivery boy and mark as out for delivery
    cancelOrder,
    acceptOrder, // Accept pending order
    rejectOrder, // Reject pending order
    getOrdersByStatus,
    getPendingAcceptanceOrders, // Get orders pending acceptance
    getOrderById, // Get single order
    getOrdersByDate, // Get orders for specific date
    getStats, // Get stats for selected outlet or specific outlet
    getAllOutletsStats, // Get stats for all outlets (used in OutletComparison)
    requestNotificationPermission
  };

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
};
